import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import progressService from '../services/progressService';

const AuthGuard = ({ children, requireAuth = true }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);
  
  const token = localStorage.getItem('registration-token');
  const approvedEmail = localStorage.getItem('approved-email');
  const isAuthenticated = !!(token && approvedEmail);

  useEffect(() => {
    const handleAuthCheck = async () => {
      try {
        // Simple case: Not authenticated trying to access protected route
        if (requireAuth && !isAuthenticated) {
          console.log('❌ AuthGuard: Not authenticated, need auth');
          setRedirectPath('/');
          setShouldRedirect(true);
          return;
        }

        // Simple case: Authenticated trying to access auth page (login)
        if (!requireAuth && isAuthenticated) {
          console.log('✅ AuthGuard: Authenticated user on login page, finding correct route...');
          console.log('🔍 Current pathname:', window.location.pathname);
          
          try {
            const progress = await progressService.getCurrentProgress(approvedEmail);
            const currentStep = progress.registration_step;
            const exactPosition = await progressService.getExactCurrentPosition(approvedEmail);
            
            console.log('📊 User progress:', currentStep);
            console.log('📍 Exact position route:', exactPosition);
            console.log('📋 Form data keys:', Object.keys(progress.form_data || {}));
            console.log('📋 Investment type from form data:', progress.form_data?.investment_type);
            console.log('📋 Has subscription:', progress.has_subscription);
            console.log('📋 Subscription status:', progress.subscription_status);
            console.log('📋 Current step in form:', progress.form_data?.current_step);
            
            // Only redirect if they're on the root auth page
            if (window.location.pathname === '/') {
              console.log('🔄 AuthGuard: Redirecting from root to:', exactPosition);
              setRedirectPath(exactPosition);
              setShouldRedirect(true);
            } else {
              console.log('⏸️ AuthGuard: Not on root, staying put');
            }
          } catch (error) {
            console.error('Error getting progress, using fallback routing:', error);
            // Fallback routing
            const invType = localStorage.getItem('investment-type');
            const orgName = localStorage.getItem('organization-name');
            const entityType = localStorage.getItem('entity-type');
            const passwordSetupComplete = localStorage.getItem('password-setup-complete');
            
            console.log('🔄 Fallback routing - localStorage values:');
            console.log('  - investment-type:', invType);
            console.log('  - organization-name:', orgName);
            console.log('  - entity-type:', entityType);
            console.log('  - password-setup-complete:', passwordSetupComplete);
            
            if (invType && (orgName || invType === 'individual') && entityType) {
              setRedirectPath('/subscription');
            } else if (invType) {
              setRedirectPath('/organization-setup');
            } else if (passwordSetupComplete) {
              setRedirectPath('/investment-type');
            } else {
              setRedirectPath('/investment-type');
            }
            setShouldRedirect(true);
          }
          return;
        }

        // If we get here, user is either:
        // 1. Authenticated accessing a protected route (allow through)
        // 2. Not authenticated accessing a public route (allow through)
        console.log('✅ AuthGuard: Access granted');

      } catch (error) {
        console.error('AuthGuard error:', error);
        if (requireAuth && !isAuthenticated) {
          setRedirectPath('/');
          setShouldRedirect(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Small delay to prevent race conditions
    const timeoutId = setTimeout(handleAuthCheck, 100);
    
    return () => clearTimeout(timeoutId);
  }, [requireAuth, isAuthenticated, approvedEmail]);

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (shouldRedirect && redirectPath) {
    console.log('🔄 AuthGuard: Redirecting to:', redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default AuthGuard; 